import React, { useState } from 'react'
import { MdOutlineRemoveRedEye } from 'react-icons/md'
import { IoMdEyeOff } from 'react-icons/io'
import { toast } from 'react-toastify'
import PropTypes from 'prop-types'
import { ChangePasswordAPI } from 'src/services/ApiService'
import ModalComponent from 'src/Common/ModalComponent'
import Button from 'src/Common/Button'

const PasswordInput = ({ label, field, value, showPassword, onToggle, onChange, disabled }) => (
  <div className="mb-3 w-100">
    <div className="pt-1 pb-1 Label_Font">{label}</div>
    <div className="d-flex w-100">
      <input
        type={showPassword ? 'text' : 'password'}
        className="form-control"
        placeholder={`Enter ${label}`}
        value={value}
        onChange={(e) => onChange(field, e.target.value)}
        disabled={disabled}
      />
      <div className="eyebackground" onClick={() => onToggle(field)} style={{ cursor: 'pointer' }}>
        {showPassword ? <MdOutlineRemoveRedEye size={20} /> : <IoMdEyeOff size={20} />}
      </div>
    </div>
  </div>
)

PasswordInput.propTypes = {
  label: PropTypes.string.isRequired,
  field: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  showPassword: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
}

const ChangePassword = ({ show, onClose }) => {
  const [formData, setFormData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  })

  const [showPasswords, setShowPasswords] = useState({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false,
  })

  const [passwordError, setPasswordError] = useState('')
  const [loading, setLoading] = useState(false)

  const passwordRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,}$/

  const validatePassword = (pwd) => passwordRegex.test(pwd)

  const toggleVisibility = (field) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }))
  }

  const handleChange = (field, value) => {
    const trimmed = value.trim()
    setFormData((prev) => ({ ...prev, [field]: trimmed }))

    if (field === 'newPassword') {
      if (trimmed.length > 0 && trimmed.length < 6) {
        setPasswordError('Password must be at least 6 characters long.')
      } else if (trimmed.length >= 6 && !validatePassword(trimmed)) {
        setPasswordError('Must contain uppercase, lowercase, number, and special character.')
      } else if (formData.confirmPassword && trimmed !== formData.confirmPassword) {
        setPasswordError('New Password and Confirm Password do not match.')
      } else {
        setPasswordError('')
      }
    }

    if (field === 'confirmPassword') {
      setPasswordError(
        trimmed !== formData.newPassword ? 'New Password and Confirm Password do not match.' : ''
      )
    }
  }

  const handleClose = () => {
    setFormData({ oldPassword: '', newPassword: '', confirmPassword: '' })
    setShowPasswords({ oldPassword: false, newPassword: false, confirmPassword: false })
    setPasswordError('')
    onClose()
  }

  const handleSubmit = async () => {
    if (!formData.oldPassword) {
      toast.error('Please enter your current password.')
      return
    }
    if (!validatePassword(formData.newPassword)) {
      toast.error('New password does not meet the required criteria.')
      return
    }
    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('Passwords do not match.')
      return
    }
    if (formData.oldPassword === formData.newPassword) {
      toast.error('New password must be different from current password.')
      return
    }

    try {
      setLoading(true)

      // ChangePasswordAPI returns parsed JSON or undefined (on unexpected errors)
      const response = await ChangePasswordAPI({
        oldPassword: formData.oldPassword,
        newPassword: formData.newPassword,
        confirmPassword: formData.confirmPassword
      })

      // Success: status 200/201 with success flag or status string
      if (response.status === 'SUCCESS' || response.success === true) {
        toast.success(response.message)
        handleClose()
        return
      }

      // Known error responses: 400, 401, 404, 409, 500, 403
      // All return JSON with an error or message field
      toast.error(response.error || response.message)

    } catch (e) {
      // Catches anything not already handled inside ChangePasswordAPI
      console.error('ChangePassword error:', e)
      const errorMessage =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.message

      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const isSubmitDisabled =
    !formData.oldPassword ||
    !formData.newPassword ||
    !formData.confirmPassword ||
    !!passwordError ||
    loading

  return (
    <ModalComponent
      show={show}
      onClick={handleClose}
      Title={'Change Password'}
      backdrop={'backdrop'}
      isTitle={true}
      HeaderclassName="modalheader"
      size={'md'}
      Content={
        <>
          <p className="signsubtitle mb-3">Update your account password</p>
          <div className="p-3" style={{ background: '#f2f2f2', borderRadius: '10px' }}>

            <PasswordInput
              label="Current Password"
              field="oldPassword"
              value={formData.oldPassword}
              showPassword={showPasswords.oldPassword}
              onToggle={toggleVisibility}
              onChange={handleChange}
            />

            <PasswordInput
              label="New Password"
              field="newPassword"
              value={formData.newPassword}
              showPassword={showPasswords.newPassword}
              onToggle={toggleVisibility}
              onChange={handleChange}
            />

            {passwordError && (
              <div style={{ color: 'red', fontSize: '12px', marginBottom: '8px' }}>
                {passwordError}
              </div>
            )}

            <PasswordInput
              label="Confirm New Password"
              field="confirmPassword"
              value={formData.confirmPassword}
              showPassword={showPasswords.confirmPassword}
              onToggle={toggleVisibility}
              onChange={handleChange}
              disabled={!validatePassword(formData.newPassword)}
            />

            <div className="d-flex justify-content-end gap-3 pt-3">
              <Button label="Cancel" className="cancelbutton" onClick={handleClose} />
              <Button
                label={loading ? 'Updating...' : 'Update Password'}
                className="addbutton"
                disabled={isSubmitDisabled}
                onClick={handleSubmit}
              />
            </div>
          </div>
        </>
      }
    />
  )
}

ChangePassword.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
}

export default ChangePassword